/**
 * Individual Documentation Section Components
 * Extracted from DocumentationPage for Apple-style floating labels
 */

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Code block component
const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code }) => (
  <div className="relative group">
    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
      <button
        onClick={() => navigator.clipboard.writeText(code)}
        className="px-3 py-1.5 rounded-md text-xs"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: '#FFFFFF',
          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
          fontWeight: 500,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          cursor: 'pointer'
        }}
      >
        Copy
      </button>
    </div>
    <pre className="p-4 rounded-lg overflow-x-auto" style={{ 
      backgroundColor: '#1A1A1A', 
      color: '#2ECC71', 
      fontFamily: "'Monaco', 'Courier New', monospace", 
      fontSize: '13px',
      lineHeight: '1.6'
    }}>
      <code>{code}</code>
    </pre>
  </div>
);

export const GettingStartedSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Getting Started
      </h2>
      
      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>What is Mernaki?</h3>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Mernaki is a modern recruitment experience platform that helps companies present their job openings with clarity, professionalism, and premium design quality. Instead of outdated career pages, Mernaki replaces them with beautiful, responsive, conversion-optimized job pages—built to impress candidates and simplify hiring.
        </p>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Mernaki is designed for companies that want:
        </p>
        <ul className="ml-6 space-y-2">
          {[
            'A clean, polished, premium design with meticulous attention to detail',
            'A faster, more enjoyable application experience', 
            'A branded job page that feels trustworthy and high-quality',
            'A plug-and-play system their IT team can deploy within minutes'
          ].map((item, i) => (
            <li key={i} style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8', listStyleType: 'disc' }}>{item}</li>
          ))}
        </ul>
        
        <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(231, 76, 60, 0.08)', border: '1px solid rgba(231, 76, 60, 0.2)' }}>
          <p style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>
            No More:
          </p>
          <ul className="space-y-1">
            {[
              'Bloated WordPress plugins',
              'Complicated CMS setups', 
              'Unreliable third-party career portals'
            ].map((item, i) => (
              <li key={i} style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', listStyleType: 'disc', marginLeft: '1rem' }}>
                {item}
              </li>
            ))}
          </ul>
          <p style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', marginTop: '8px', fontWeight: 500 }}>
            Just a modern recruitment page that works.
          </p>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Why Mernaki Exists</h3>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Most companies struggle with one of these problems:
        </p>
        <ul className="ml-6 space-y-2 mb-6">
          {[
            'Their current job page looks outdated',
            'Their career portal is slow or confusing',
            'Applicants drop off because the design feels unprofessional',
            'They rely on external job platforms that don\'t represent their brand',
            'Updating job pages requires a developer or designer'
          ].map((item, i) => (
            <li key={i} style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8', listStyleType: 'disc' }}>{item}</li>
          ))}
        </ul>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Mernaki solves all of this by giving businesses an instant, branded job page that feels like it was built by a professional UI/UX team—without needing one.
        </p>
      </div>

      <div className="p-6 rounded-xl mb-8" style={{ backgroundColor: 'rgba(46, 204, 113, 0.08)', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
        <p style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8', fontWeight: 600 }}>
          Mernaki Philosophy
        </p>
        <p style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7' }}>
          Mernaki believes that hiring is a company's first impression. A clean, modern job page builds trust, attracts better talent, and raises the perceived value of the entire brand.
        </p>
      </div>
    </motion.div>
  );
};

export const QuickStartSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, delay: 0.1, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        How It Works
      </h2>
      
      <p className="mb-8" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Mernaki gives you everything you need to create professional job pages:
      </p>

      <div className="space-y-8">
        {/* Beautiful Job Page Template */}
        <div className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.02]" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid rgba(0, 0, 0, 0.06)', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)' }}>
          <div className="flex gap-5">
            {/* Premium Template Icon */}
            <div className="flex-shrink-0 relative">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center relative overflow-hidden group" 
                style={{ 
                  backgroundColor: 'rgba(74, 144, 226, 0.08)',
                  border: '1px solid rgba(74, 144, 226, 0.15)',
                  boxShadow: '0 4px 16px rgba(74, 144, 226, 0.08)'
                }}
              >
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    backgroundColor: 'rgba(74, 144, 226, 0.12)'
                  }}
                />
                
                {/* Template/Layout Icon */}
                <div 
                  className="relative z-10 transition-transform duration-300 group-hover:scale-110"
                  style={{ color: '#4A90E2' }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="9" y1="9" x2="15" y2="9" stroke="currentColor" strokeWidth="2"/>
                    <line x1="9" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="2"/>
                    <line x1="9" y1="15" x2="13" y2="15" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-xl mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>A Beautiful Job Page Template</h4>
              <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7' }}>
                Fully designed by experts, optimized for:
              </p>
              <ul className="ml-4 space-y-1">
                {['Conversion', 'Mobile first', 'Speed', 'Trust-building visuals'].map((item, i) => (
                  <li key={i} style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', listStyleType: 'disc' }}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Brand Customization Tools */}
        <div className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.02]" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid rgba(0, 0, 0, 0.06)', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)' }}>
          <div className="flex gap-5">
            {/* Premium Customization Icon */}
            <div className="flex-shrink-0 relative">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center relative overflow-hidden group" 
                style={{ 
                  backgroundColor: 'rgba(46, 204, 113, 0.08)',
                  border: '1px solid rgba(46, 204, 113, 0.15)',
                  boxShadow: '0 4px 16px rgba(46, 204, 113, 0.08)'
                }}
              >
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    backgroundColor: 'rgba(46, 204, 113, 0.12)'
                  }}
                />
                
                {/* Palette/Customization Icon */}
                <div 
                  className="relative z-10 transition-transform duration-300 group-hover:scale-110"
                  style={{ color: '#2ECC71' }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/>
                    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
                    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>
                    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-xl mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Brand Customization Tools</h4>
              <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7' }}>
                From your dashboard you can customize:
              </p>
              <ul className="ml-4 space-y-1">
                {[
                  'Logo', 'Colors', 'Typography', 'Layout', 'Images', 
                  'Job description sections', 'Multi-language support (DE/EN)'
                ].map((item, i) => (
                  <li key={i} style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', listStyleType: 'disc' }}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Deployment-Ready Package */}
        <div className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.02]" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid rgba(0, 0, 0, 0.06)', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)' }}>
          <div className="flex gap-5">
            {/* Premium Package Icon */}
            <div className="flex-shrink-0 relative">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center relative overflow-hidden group" 
                style={{ 
                  backgroundColor: 'rgba(155, 89, 182, 0.08)',
                  border: '1px solid rgba(155, 89, 182, 0.15)',
                  boxShadow: '0 4px 16px rgba(155, 89, 182, 0.08)'
                }}
              >
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    backgroundColor: 'rgba(155, 89, 182, 0.12)'
                  }}
                />
                
                {/* Package/Box Icon */}
                <div 
                  className="relative z-10 transition-transform duration-300 group-hover:scale-110"
                  style={{ color: '#9B59B6' }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="m3.29 7 8.71 5 8.71-5" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 22V12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-xl mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Deployment-Ready Package</h4>
              <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7' }}>
                Your IT team gets a ZIP containing:
              </p>
              <ul className="ml-4 space-y-1">
                {[
                  'HTML/CSS/JS', 'Assets', 'Configuration files', 
                  'Optional integrations (Forms, Email, APIs)'
                ].map((item, i) => (
                  <li key={i} style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', listStyleType: 'disc' }}>{item}</li>
                ))}
              </ul>
              <p className="mt-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '14px', fontWeight: 600 }}>
                They simply place it on the server. Nothing else required.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: 'rgba(74, 144, 226, 0.08)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
        <p style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
          Fast Setup: 5–10 minutes
        </p>
        <p style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '15px', lineHeight: '1.7' }}>
          IT teams love how simple the ZIP package is. Whether your company posts 1 job or 100 jobs, Mernaki ensures they all look professional and consistent.
        </p>
      </div>
    </motion.div>
  );
};

export const ServicesSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Who It's For
      </h2>
      
      {/* Who Mernaki Is For */}
      <div className="mb-12">
        <h3 className="text-2xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Who Mernaki Is For</h3>
        <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
          Mernaki is made for:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            'Medium-sized businesses',
            'Startups scaling fast', 
            'Businesses wanting a premium brand image',
            'Companies frustrated with bad job pages',
            'Teams that want plug-and-play simplicity'
          ].map((target, i) => (
            <div key={i} className="flex items-center gap-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <div 
                className="w-2 h-2 rounded-full relative" 
                style={{ 
                  backgroundColor: '#1A1A1A',
                  boxShadow: '0 1px 3px rgba(26, 26, 26, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                }} 
              />
              <span style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '15px', fontWeight: 500 }}>{target}</span>
            </div>
          ))}
        </div>
      </div>

      {/* What Makes Mernaki Different */}
      <div className="mb-12">
        <h3 className="text-2xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>What Makes Mernaki Different</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { 
              title: '1. Premium Apple-like Design', 
              desc: 'Minimalistic. Smooth. High-end. No gradients. No clutter. No "AI-generated look."',
              color: '#4A90E2'
            },
            { 
              title: '2. Standalone Deployment', 
              desc: 'No CMS. No backend required. Works on any server.',
              color: '#2ECC71'
            },
            { 
              title: '3. Fast Setup (5–10 minutes)', 
              desc: 'IT teams love how simple the ZIP package is.',
              color: '#E74C3C'
            },
            { 
              title: '4. SEO-Optimized', 
              desc: 'Every job page is structured to appear on Google Jobs and organic search.',
              color: '#F39C12'
            },
            { 
              title: '5. Fully Branded', 
              desc: 'Matches the company\'s brand perfectly—colors, shapes, layout.',
              color: '#9B59B6'
            },
            { 
              title: '6. Zero Lock-In', 
              desc: 'Companies keep the files. Host wherever they want. Modify however they want.',
              color: '#1ABC9C'
            }
          ].map((differentiator, i) => (
            <div key={i} className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <div className="flex items-start gap-3 mb-3">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                  style={{ backgroundColor: differentiator.color }}
                />
                <h4 className="text-lg" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
                  {differentiator.title}
                </h4>
              </div>
              <p style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '15px', lineHeight: '1.7', marginLeft: '24px' }}>
                {differentiator.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Brand Statement */}
      <div className="p-8 rounded-xl" style={{ backgroundColor: 'rgba(46, 204, 113, 0.08)', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
        <h4 className="text-xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
          Short Brand Statement
        </h4>
        <p style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8', fontWeight: 500 }}>
          Mernaki delivers premium job pages that help companies attract better talent through modern design, effortless customization, and fast deployment.
        </p>
        <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(46, 204, 113, 0.2)' }}>
          <p style={{ color: '#2ECC71', fontFamily: "'Inter', sans-serif", fontSize: '18px', fontWeight: 600, textAlign: 'center' }}>
            Elegant design + simple deployment = Mernaki
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export const ApiReferenceSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        API Reference
      </h2>
      
      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Mernaki provides a comprehensive RESTful API for programmatic access to all services.
      </p>

      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Base URL</h3>
        <CodeBlock code="https://api-gateway.jbplatform.internal/v1" />
      </div>

      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Common Endpoints</h3>
        <div className="space-y-4">
          <div className="p-5 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <h4 className="text-lg mb-2" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>List Services</h4>
            <CodeBlock code="GET /services" />
            <p className="mt-2" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '14px' }}>Returns all available Mernaki services</p>
          </div>
          
          <div className="p-5 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <h4 className="text-lg mb-2" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Get Service Status</h4>
            <CodeBlock code="GET /services/:id/status" />
            <p className="mt-2" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '14px' }}>Check operational status of a specific service</p>
          </div>
        </div>
      </div>

      <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(74, 144, 226, 0.08)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
        <p style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7', fontWeight: 600 }}>
          Full API documentation with examples is available for enterprise clients. Contact support@mernaki.de for access.
        </p>
      </div>
    </motion.div>
  );
};

export const AuthenticationSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, delay: 0.35, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Authentication
      </h2>
      
      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Mernaki uses API keys for authentication. Include your API key in the Authorization header of each request.
      </p>

      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Request Example</h3>
        <CodeBlock code={`curl -X GET "https://api-gateway.jbplatform.internal/v1/services" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`} />
      </div>

      <div className="mb-8">
        <h3 className="text-2xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Getting Your API Key</h3>
        <ol className="ml-6 space-y-2">
          {['Log in to your Mernaki dashboard', 'Navigate to Settings → API Keys', 'Click "Generate New Key"', 'Copy and securely store your API key'].map((item, i) => (
            <li key={i} style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8', listStyleType: 'decimal' }}>{item}</li>
          ))}
        </ol>
      </div>

      <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(231, 76, 60, 0.08)', border: '1px solid rgba(231, 76, 60, 0.2)' }}>
        <p style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '15px', lineHeight: '1.7', fontWeight: 600 }}>
          Security: Never expose your API keys in client-side code or public repositories. Always use environment variables.
        </p>
      </div>
    </motion.div>
  );
};

// Additional sections with Mernaki-specific content
export const IntegrationSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Integration
      </h2>
      
      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Mernaki integrates seamlessly with your existing infrastructure and tools.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { title: 'Email Integration', desc: 'Connect with your existing email systems for application notifications' },
          { title: 'ATS Integration', desc: 'Works with popular Applicant Tracking Systems' },
          { title: 'Analytics Integration', desc: 'Google Analytics, Facebook Pixel, and custom tracking' },
          { title: 'Form Integration', desc: 'Custom application forms and data collection' }
        ].map((integration, i) => (
          <div key={i} className="p-5 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <h4 className="text-lg mb-2" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>{integration.title}</h4>
            <p style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', lineHeight: '1.6' }}>{integration.desc}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export const CustomizationSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Customization
      </h2>
      
      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Customize every aspect of your job pages to match your brand perfectly.
      </p>

      <div className="space-y-6">
        {[
          { category: 'Visual Branding', items: ['Logo placement', 'Color schemes', 'Typography', 'Layout options'] },
          { category: 'Content Customization', items: ['Job descriptions', 'Company information', 'Benefits sections', 'Application process'] },
          { category: 'Advanced Options', items: ['Multi-language support', 'Custom CSS', 'JavaScript integration', 'Third-party widgets'] }
        ].map((section, i) => (
          <div key={i} className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <h4 className="text-lg mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>{section.category}</h4>
            <ul className="grid grid-cols-2 gap-2">
              {section.items.map((item, j) => (
                <li key={j} className="flex items-center gap-2">
                  <div 
                    className="w-1.5 h-1.5 rounded-full" 
                    style={{ 
                      backgroundColor: '#1A1A1A',
                      boxShadow: '0 1px 2px rgba(26, 26, 26, 0.15)'
                    }} 
                  />
                  <span style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export const DeploymentSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Deployment
      </h2>
      
      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Deploy your Mernaki pages in minutes with our simple ZIP package.
      </p>

      <div className="space-y-4">
        {[
          { 
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ),
            title: 'Download ZIP Package', 
            desc: 'Get your customized job page files' 
          },
          { 
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ),
            title: 'Upload to Server', 
            desc: 'Place files in your web server directory' 
          },
          { 
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2"/>
                <path d="m12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            ),
            title: 'Configure Domain', 
            desc: 'Point your subdomain to the files' 
          },
          { 
            icon: (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ),
            title: 'Test & Launch', 
            desc: 'Verify everything works and go live' 
          }
        ].map((step, i) => (
          <div key={i} className="flex gap-5 p-6 rounded-xl transition-all duration-300 hover:scale-[1.02]" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid rgba(0, 0, 0, 0.06)', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)' }}>
            {/* Premium SVG Icon */}
            <div className="flex-shrink-0 relative">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center relative overflow-hidden group" 
                style={{ 
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                }}
              >
                {/* Animated background on hover */}
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.05) 0%, rgba(26, 26, 26, 0.02) 100%)'
                  }}
                />
                
                {/* SVG Icon */}
                <div 
                  className="relative z-10 transition-transform duration-300 group-hover:scale-110"
                  style={{ 
                    color: '#1A1A1A'
                  }}
                >
                  {step.icon}
                </div>
              </div>
              
              {/* Subtle outer shadow */}
              <div
                className="absolute inset-0 rounded-2xl -z-10"
                style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  filter: 'blur(6px)',
                  transform: 'scale(1.05) translateY(2px)'
                }}
              />
            </div>
            <div className="flex-1">
              <h4 className="text-xl mb-2" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600, letterSpacing: '-0.01em' }}>{step.title}</h4>
              <p style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '15px', lineHeight: '1.6' }}>{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export const TroubleshootingSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Troubleshooting
      </h2>
      
      <p className="mb-6" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Common issues and solutions for Mernaki deployment and customization.
      </p>

      <div className="space-y-4">
        {[
          { issue: 'Pages not loading correctly', solution: 'Check file permissions and server configuration' },
          { issue: 'Styling not appearing', solution: 'Verify CSS files are uploaded and paths are correct' },
          { issue: 'Forms not submitting', solution: 'Configure email settings and form endpoints' },
          { issue: 'Mobile display issues', solution: 'Ensure responsive CSS is not being overridden' }
        ].map((item, i) => (
          <div key={i} className="p-5 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <h4 className="text-lg mb-2" style={{ color: '#E74C3C', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
              {item.issue}
            </h4>
            <p style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', lineHeight: '1.6' }}>
              {item.solution}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export const SupportSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  return (
    <motion.div 
      ref={ref}
      className="mb-20" 
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
      animate={isInView ? { opacity: 1, y: 0 } : {}} 
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
    >
      {/* Main Section Header */}
      <h2 className="text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
        Support
      </h2>
      
      <p className="mb-8" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
        Get help when you need it with our comprehensive support options.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { title: 'Documentation', desc: 'Comprehensive guides and tutorials', link: '/docs' },
          { title: 'Help Center', desc: 'FAQ and common solutions', link: '/help' },
          { title: 'Contact Support', desc: 'Direct support for technical issues', link: '/contact' },
          { title: 'Community', desc: 'Connect with other Mernaki users', link: '#' }
        ].map((support, i) => (
          <div key={i} className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <h4 className="text-lg mb-2" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>{support.title}</h4>
            <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '14px', lineHeight: '1.6' }}>{support.desc}</p>
            <a 
              href={support.link}
              className="text-sm"
              style={{ color: '#4A90E2', fontFamily: "'Inter', sans-serif", fontWeight: 500, textDecoration: 'none' }}
            >
              Learn more →
            </a>
          </div>
        ))}
      </div>

      <div className="mt-8 p-6 rounded-xl text-center" style={{ backgroundColor: 'rgba(46, 204, 113, 0.08)', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
        <h4 className="text-xl mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
          Need Help Getting Started?
        </h4>
        <p className="mb-4" style={{ color: '#666666', fontFamily: "'Inter', sans-serif", fontSize: '15px', lineHeight: '1.7' }}>
          Our team is here to help you create the perfect job pages for your company.
        </p>
        <a 
          href="/contact"
          className="inline-block px-6 py-3 rounded-lg"
          style={{ 
            backgroundColor: '#2ECC71', 
            color: '#FFFFFF', 
            fontFamily: "'Inter', sans-serif", 
            fontWeight: 600,
            textDecoration: 'none',
            fontSize: '14px'
          }}
        >
          Contact Support
        </a>
      </div>
    </motion.div>
  );
};